// ============================================================
// TPISection.tsx
// TPI form + autocomplete from the firm's saved TPI directory.
//
// Redesign (final sections pass): the shared SectionCards view/edit cards
// in the case shell (SELF_FRAMED) — Insurer (insurer combobox + reference,
// with "Save to firm directory") and Address — plus the glance strip and
// the shared section footer.
//
// Not PartyContactSection: the TPI is the nested currentCase.tpi object
// ({ insurer, referenceNumber, address: Address|null }) with a REQUIRED
// insurer, a firm-directory combobox and save-for-reuse — no phone /
// email / notes.
//
// LOGIC UNCHANGED:
//   • Insurer is a combobox: focusing or typing surfaces saved TpiContacts
//     (substring match on insurer, alphabetical); ArrowUp/Down/Enter/Esc/
//     Tab; picking one replaces all 7 case-level TPI fields in ONE
//     updateCase + broadcastFieldEdits(TPI_ALL_FIELD_KEYS)
//   • "Save TPI for future reuse" POSTs (or, for a duplicate insurer name,
//     confirms via ConfirmDialogue then PATCHes) the firm-wide directory
//   • address helper (null → all-empty overlay); AddressLookup select/clear
//     writes all 5 in ONE update + broadcastFieldEdits(TPI_ADDRESS_FIELD_KEYS)
//   • same fieldKeys (tpi.insurer / reference-number / address-* /
//     address-search) for presence + live sync; postcode uppercased
//   • "Insurer name is required" gates Mark complete (shared footer) →
//     markComplete('tpi', next) + advanceCaseSection
// ============================================================
import React, { useEffect, useRef, useState } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { TPI, Address, TpiContact } from '../../types/case.types'
import {
  createTpiContactApi,
  updateTpiContactApi,
  TpiContactInput,
} from '../../api/casevoApi'
import FormField from '../shared/FormField'
import FieldError from '../shared/FieldError'
import AddressLookup from '../shared/AddressLookup'
import { broadcastFieldEdits } from '../../hooks/usePresence'
import { flushAutoSave } from '../../hooks/useAutoSave'
import type { ValidationError } from '../../utils/validation'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import { inpCls, FGRID } from '../clientInfo/clientInfoRegistry'
import { BTN_SEC, SM } from '../clientInfo/ClientInfoSections'
import SectionCards, { type CardDef } from '../case/sections/SectionCards'
import { KIcon, Glance, GlanceDash, SectionFooter, Kv, Empty, AddressBlock, DL, BIG, MONO, CHIP } from '../case/sections/sectionKit'
import { addressLinesOf } from './DefendantSection'

const SAVED_FLASH_MS = 2000
// Live edit-sync: address-lookup + the insurer-directory pick both write
// multiple TPI fields programmatically (no per-field blur), so they broadcast
// the affected fieldKeys explicitly.
const TPI_ADDRESS_FIELD_KEYS = ['tpi.address-line-1', 'tpi.address-line-2', 'tpi.town', 'tpi.county', 'tpi.postcode']
const TPI_ALL_FIELD_KEYS = ['tpi.insurer', 'tpi.reference-number', ...TPI_ADDRESS_FIELD_KEYS]
const ADDRESS_SENTINEL = 'tpi.__address'
const FIELD_KEY: Record<keyof TPI, string> = { insurer: 'tpi.insurer', referenceNumber: 'tpi.reference-number', address: ADDRESS_SENTINEL }
const broadcast = (keys: string[]): void =>
  broadcastFieldEdits(keys.flatMap((k) => (k === ADDRESS_SENTINEL ? TPI_ADDRESS_FIELD_KEYS : [k])))
const EMPTY_ADDR: Address = { line1: '', line2: '', town: '', county: '', postcode: '' }

const TPISection: React.FC = () => {
  const {
    currentCase,
    updateCase,
    tpiContacts,
    addTpiContact,
    replaceTpiContact,
  } = useCasevoStore()
  // ── Autocomplete state ──────────────────────────────────
  const [dropdownOpen, setDropdownOpen] = useState(false)
  // -1 = nothing highlighted yet (mouse hasn't entered, keyboard
  // hasn't been pressed). 0..n-1 = highlighted index in the
  // filtered list. Wraps around top/bottom on arrow keys.
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // ── Save-for-reuse state ────────────────────────────────
  // Combined into one enum-ish state so we don't have to coordinate
  // multiple booleans. 'saved' state fades back to 'idle' after a
  // short timer.
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  // Pending overwrite of an existing firm-directory TPI entry — drives the
  // ConfirmDialogue (replaces the old native window.confirm). null = closed.
  const [pendingTpiUpdate, setPendingTpiUpdate] = useState<
    { id: string; insurer: string; payload: TpiContactInput } | null
  >(null)
  const [saveErrorMsg, setSaveErrorMsg] = useState<string | null>(null)
  const savedFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Tidy timer on unmount.
  useEffect(() => {
    return () => {
      if (savedFlashTimerRef.current) clearTimeout(savedFlashTimerRef.current)
    }
  }, [])
  // Click-outside handler — closes the dropdown when the user clicks
  // anywhere outside the combobox wrapper. Uses mousedown rather
  // than click so it fires before the input's onBlur fights with
  // an option's onMouseDown handler.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
        setHighlightedIndex(-1)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])
  if (!currentCase) return null
  const tpi = currentCase.tpi
  // Spread the LIVE store object so a write never reverts a field a
  // colleague changed meanwhile (same as Defendant / Accident).
  const liveTpi = (): TPI => useCasevoStore.getState().currentCase?.tpi ?? tpi
  const update = (field: keyof TPI, value: unknown) => {
    updateCase({ tpi: { ...liveTpi(), [field]: value } })
  }
  const updateMany = (changes: Partial<TPI>): void => {
    updateCase({ tpi: { ...liveTpi(), ...changes } })
  }
  // Address helper — single updateCase per keystroke, treating
  // null as all-empty and overlaying the changed sub-field.
  const updateAddress = (changes: Partial<Address>) => {
    const a = liveTpi().address
    update('address', {
      line1: a?.line1 ?? '',
      line2: a?.line2 ?? '',
      town: a?.town ?? '',
      county: a?.county ?? '',
      postcode: a?.postcode ?? '',
      ...changes,
    })
  }
  // Filtered TpiContacts. Empty query → show everything. Non-empty
  // → case-insensitive substring match on insurer name only (matches
  // the spec — town/postcode aren't searchable). Sorted alphabetically
  // by insurer so the dropdown is predictable.
  const query = tpi.insurer.trim().toLowerCase()
  const filteredContacts = tpiContacts
    .filter((c) => query === '' || c.insurer.toLowerCase().includes(query))
    .sort((a, b) => a.insurer.localeCompare(b.insurer))
  const showDropdown = dropdownOpen && filteredContacts.length > 0
  // Picking a contact: replace all 7 case-level TPI fields in one
  // updateCase call. Then close the dropdown.
  const handleSelect = (contact: TpiContact) => {
    updateCase({
      tpi: {
        insurer: contact.insurer,
        referenceNumber: contact.referenceNumber,
        address: {
          line1: contact.addressLine1,
          line2: contact.addressLine2,
          town: contact.addressTown,
          county: contact.addressCounty,
          postcode: contact.addressPostcode,
        },
      },
    })
    setDropdownOpen(false)
    setHighlightedIndex(-1)
    // Programmatic multi-field write (7 TPI fields) — broadcast for live sync.
    broadcastFieldEdits(TPI_ALL_FIELD_KEYS)
    // Return focus to the input so further keyboard nav works
    // (e.g. user immediately starts typing to refine).
    inputRef.current?.focus()
  }
  // Keyboard on the Insurer input. ArrowDown opens the dropdown if
  // closed; otherwise navigates. Enter selects when something's
  // highlighted (so a stray Enter on an empty highlight doesn't
  // accidentally submit anything). Escape closes without changing.
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (filteredContacts.length === 0) {
      // No-op for arrow keys when there's nothing to navigate.
      if (e.key === 'Escape' && dropdownOpen) {
        setDropdownOpen(false)
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!dropdownOpen) setDropdownOpen(true)
      setHighlightedIndex((prev) =>
        prev < 0 ? 0 : (prev + 1) % filteredContacts.length,
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (!dropdownOpen) setDropdownOpen(true)
      setHighlightedIndex((prev) =>
        prev <= 0 ? filteredContacts.length - 1 : prev - 1,
      )
    } else if (e.key === 'Enter') {
      if (dropdownOpen && highlightedIndex >= 0 && filteredContacts[highlightedIndex]) {
        e.preventDefault()
        handleSelect(filteredContacts[highlightedIndex])
      }
    } else if (e.key === 'Escape') {
      if (dropdownOpen) {
        e.preventDefault()
        setDropdownOpen(false)
        setHighlightedIndex(-1)
      }
    } else if (e.key === 'Tab') {
      // Let Tab move focus naturally — just close the dropdown.
      setDropdownOpen(false)
    }
  }
  // ── Save-for-reuse ──────────────────────────────────────
  const saveDisabled = !tpi.insurer.trim() || saveState === 'saving'
  const handleSaveForReuse = async () => {
    if (saveDisabled) return
    setSaveErrorMsg(null)
    const insurerKey = tpi.insurer.trim().toLowerCase()
    const existing = tpiContacts.find(
      (c) => c.insurer.trim().toLowerCase() === insurerKey,
    )
    const payload: TpiContactInput = {
      insurer: tpi.insurer.trim(),
      referenceNumber: tpi.referenceNumber,
      addressLine1: tpi.address?.line1 ?? '',
      addressLine2: tpi.address?.line2 ?? '',
      addressTown: tpi.address?.town ?? '',
      addressCounty: tpi.address?.county ?? '',
      addressPostcode: tpi.address?.postcode ?? '',
    }
    if (existing) {
      // Duplicate insurer name — confirm before overwriting the firm
      // directory entry via the app's styled dialogue (replaces the old
      // native window.confirm). Commit happens in commitTpiSave on confirm.
      setPendingTpiUpdate({ id: existing.id, insurer: existing.insurer, payload })
      return
    }
    // No duplicate — create straight away.
    await commitTpiSave(payload, null)
  }
  // Shared commit path — used for a fresh create (updateId null) AND for a
  // confirmed overwrite (updateId set). Same success/error handling the
  // single handler had before; only the duplicate-confirm gate moved out.
  const commitTpiSave = async (payload: TpiContactInput, updateId: string | null) => {
    setSaveState('saving')
    try {
      if (updateId) {
        const updated = await updateTpiContactApi(updateId, payload)
        replaceTpiContact(updated)
      } else {
        const created = await createTpiContactApi(payload)
        addTpiContact(created)
      }
      setSaveState('saved')
      if (savedFlashTimerRef.current) clearTimeout(savedFlashTimerRef.current)
      savedFlashTimerRef.current = setTimeout(() => {
        setSaveState('idle')
      }, SAVED_FLASH_MS)
    } catch (err) {
      setSaveErrorMsg(err instanceof Error ? err.message : 'Failed to save TPI')
      setSaveState('error')
    }
  }
  const errors: ValidationError[] = tpi.insurer.trim() ? [] : [{ field: 'insurer', message: 'Insurer name is required' }]
  const addr = addressLinesOf(tpi.address)
  const inDirectory = !!tpi.insurer.trim() && tpiContacts.some((c) => c.insurer.trim().toLowerCase() === tpi.insurer.trim().toLowerCase())

  const text = (
    ctx: { idPrefix: string }, id: string, label: string, value: string, onChange: (v: string) => void, fieldKey: string,
    opts: { placeholder?: string; full?: boolean; mono?: boolean } = {},
  ): React.ReactNode => (
    <div className={opts.full ? 'sm:col-span-2' : ''}>
      <FormField variant="info" label={label} fieldKey={fieldKey} htmlFor={`${ctx.idPrefix}-${id}`}>
        <input id={`${ctx.idPrefix}-${id}`} type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={opts.placeholder}
          className={`${inpCls(false)} ${opts.mono ? 'font-mono tracking-[0.04em]' : ''}`} />
      </FormField>
    </div>
  )

  // Save-for-reuse — a small secondary action in the Insurer card header.
  const saveAction = (
    <span className="inline-flex items-center gap-2 mr-1">
      {saveState === 'saved' && <span aria-live="polite" className="text-[12px] font-semibold text-casevo-brand dark:text-[#8fd6b0]">Saved</span>}
      {saveState === 'error' && saveErrorMsg && <span aria-live="polite" className="text-[12px] text-casevo-danger">{saveErrorMsg}</span>}
      <button type="button" onClick={() => { void handleSaveForReuse() }} disabled={saveDisabled}
        title={!tpi.insurer.trim() ? 'Enter an insurer first' : 'Save these details to the firm TPI directory'}
        className={`${BTN_SEC} ${SM} disabled:opacity-50 disabled:cursor-not-allowed`}>
        <KIcon name="save" size={13} />{saveState === 'saving' ? 'Saving…' : 'Save to directory'}
      </button>
    </span>
  )

  const defs: CardDef<TPI>[] = [
    {
      key: 'insurer', title: 'Insurer', sub: "The defendant's insurer", icon: <KIcon name="umbrella" size={17} />,
      fields: ['insurer', 'referenceNumber'], errorKeys: ['insurer'],
      presenceKeys: ['tpi.insurer', 'tpi.reference-number'],
      headerExtra: saveAction,
      view: (ctx) => (
        <dl className={DL}>
          <Kv label="Insurer" full>
            {tpi.insurer.trim()
              ? <span className="inline-flex items-center gap-2 flex-wrap"><span className={BIG}>{tpi.insurer}</span>{inDirectory && <span className={CHIP}><KIcon name="check" size={12} />In firm directory</span>}</span>
              : <Empty onAdd={ctx.onAdd} />}
          </Kv>
          <Kv label="Reference number" full>{tpi.referenceNumber.trim() ? <span className={MONO}>{tpi.referenceNumber}</span> : <Empty onAdd={ctx.onAdd} />}</Kv>
        </dl>
      ),
      edit: (ctx) => {
        const err = ctx.err('insurer')
        return (
          <div className={FGRID}>
            <div className="sm:col-span-2">
              {/* Insurer combobox. The wrapper is position:relative so the
                  dropdown's absolute positioning lines up under the input. */}
              <FormField variant="info" label="Insurer" fieldKey="tpi.insurer" required htmlFor={`${ctx.idPrefix}-insurer`}>
                <div ref={wrapperRef} className="relative">
                  <input
                    id={`${ctx.idPrefix}-insurer`}
                    ref={inputRef}
                    type="text"
                    value={tpi.insurer}
                    onChange={(e) => {
                      update('insurer', e.target.value)
                      setDropdownOpen(true)
                      setHighlightedIndex(-1)
                    }}
                    onFocus={() => setDropdownOpen(true)}
                    onKeyDown={handleInputKeyDown}
                    placeholder="Type to search the firm directory"
                    aria-invalid={err ? true : undefined}
                    className={inpCls(!!err)}
                    role="combobox"
                    aria-expanded={showDropdown}
                    aria-autocomplete="list"
                    aria-controls="tpi-insurer-listbox"
                    aria-activedescendant={
                      highlightedIndex >= 0 && filteredContacts[highlightedIndex]
                        ? `tpi-option-${filteredContacts[highlightedIndex].id}`
                        : undefined
                    }
                  />
                  {showDropdown && (
                    <ul
                      id="tpi-insurer-listbox"
                      role="listbox"
                      className="absolute z-20 left-0 right-0 mt-1 bg-casevo-surface border border-casevo-border rounded-[12px] p-1 shadow-[0_12px_32px_rgba(16,42,29,0.16)] dark:shadow-[0_12px_32px_rgba(0,0,0,0.45)] max-h-60 overflow-y-auto"
                    >
                      {filteredContacts.map((c, idx) => {
                        const isHighlighted = idx === highlightedIndex
                        return (
                          <li
                            key={c.id}
                            id={`tpi-option-${c.id}`}
                            role="option"
                            aria-selected={isHighlighted}
                            // onMouseDown (not onClick) so we fire BEFORE the
                            // input blurs and the click-outside handler
                            // closes the dropdown.
                            onMouseDown={(e) => {
                              e.preventDefault()
                              handleSelect(c)
                            }}
                            onMouseEnter={() => setHighlightedIndex(idx)}
                            className={`px-[10px] py-2 rounded-[8px] text-[13.5px] cursor-pointer ${
                              isHighlighted ? 'bg-casevo-mint text-casevo-forest' : 'text-casevo-forest hover:bg-casevo-input'
                            }`}
                          >
                            <div className="font-semibold">{c.insurer}</div>
                            {(c.referenceNumber || c.addressTown) && (
                              <div className="text-[12px] text-casevo-text-soft">
                                {c.referenceNumber}
                                {c.referenceNumber && c.addressTown ? ' · ' : ''}
                                {c.addressTown}
                              </div>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
                <FieldError message={err} />
              </FormField>
            </div>
            {text(ctx, 'ref', 'Reference number', tpi.referenceNumber, (v) => update('referenceNumber', v), 'tpi.reference-number', { full: true })}
          </div>
        )
      },
    },
    {
      key: 'address', title: 'Address', sub: "The insurer's claims address", icon: <KIcon name="map" size={17} />,
      fields: ['address'],
      presenceKeys: [...TPI_ADDRESS_FIELD_KEYS, 'tpi.address-search'],
      view: (ctx) => (
        <dl className={DL}>
          <Kv label="Address" full>{addr.lines.length || addr.postcode ? <AddressBlock lines={addr.lines} postcode={addr.postcode || undefined} /> : <Empty onAdd={ctx.onAdd} />}</Kv>
        </dl>
      ),
      edit: (ctx) => {
        const a = tpi.address ?? EMPTY_ADDR
        return (
          <div className={FGRID}>
            <div className="sm:col-span-2">
              <FormField variant="info" label="Search address" fieldKey="tpi.address-search" htmlFor={`${ctx.idPrefix}-search`}>
                <AddressLookup
                  variant="info"
                  inputId={`${ctx.idPrefix}-search`}
                  value={tpi.address}
                  onSelect={(picked) => { updateAddress(picked); broadcastFieldEdits(TPI_ADDRESS_FIELD_KEYS) }}
                  onClear={() => { updateAddress(EMPTY_ADDR); broadcastFieldEdits(TPI_ADDRESS_FIELD_KEYS) }}
                />
              </FormField>
            </div>
            {text(ctx, 'l1', 'Address line 1', a.line1, (v) => updateAddress({ line1: v }), 'tpi.address-line-1')}
            {text(ctx, 'l2', 'Address line 2', a.line2, (v) => updateAddress({ line2: v }), 'tpi.address-line-2')}
            {text(ctx, 'town', 'Town', a.town, (v) => updateAddress({ town: v }), 'tpi.town')}
            {text(ctx, 'county', 'County', a.county, (v) => updateAddress({ county: v }), 'tpi.county')}
            {text(ctx, 'pc', 'Postcode', a.postcode, (v) => updateAddress({ postcode: v.toUpperCase() }), 'tpi.postcode', { placeholder: 'LS6 3AB', mono: true })}
          </div>
        )
      },
    },
  ]

  const a = tpi.address
  const glance = (
    <Glance cells={[
      { icon: 'umbrella', label: 'Insurer', value: tpi.insurer.trim() || <GlanceDash />, sub: tpi.insurer.trim() ? 'Third-party insurer' : 'Required' },
      { icon: 'hash', label: 'Reference', value: tpi.referenceNumber.trim() ? <span className={`${MONO} !text-[13px]`}>{tpi.referenceNumber}</span> : <GlanceDash />, sub: tpi.referenceNumber.trim() ? 'Claim reference' : 'Not recorded' },
      {
        icon: 'map', label: 'Based in',
        value: a && (a.town.trim() || a.postcode.trim()) ? <>{a.town}{a.town.trim() && a.postcode.trim() ? ' · ' : ''}{a.postcode.trim() && <span className={`${MONO} !text-[13px]`}>{a.postcode}</span>}</> : <GlanceDash />,
        sub: a?.line1.trim() || 'Address',
      },
      { icon: 'save', label: 'Directory', value: inDirectory ? 'Saved' : <GlanceDash />, sub: inDirectory ? 'In the firm TPI directory' : 'Not saved for reuse' },
    ]} />
  )

  return (
    <>
      <SectionCards<TPI>
        value={tpi}
        onChange={updateMany}
        fieldKeyOf={(f) => FIELD_KEY[f]}
        onBroadcast={broadcast}
        onFlush={flushAutoSave}
        errors={errors}
        defs={defs}
        title="TPI (insurer)"
        sub="The third-party insurer handling the claim."
        glance={glance}
        footer={<SectionFooter sectionKey="tpi" errors={errors} />}
        idPrefix={`tpi-${currentCase.id}`}
      />
      {/* Overwrite-confirmation dialogue — the app's styled modal. */}
      <ConfirmDialogue
        isOpen={pendingTpiUpdate !== null}
        title="Update saved TPI?"
        message={
          pendingTpiUpdate
            ? `A TPI named "${pendingTpiUpdate.insurer}" is already saved for your firm. Update it with the current details?`
            : ''
        }
        confirmLabel="Update"
        onConfirm={() => {
          const p = pendingTpiUpdate
          setPendingTpiUpdate(null)
          if (p) void commitTpiSave(p.payload, p.id)
        }}
        onCancel={() => { setPendingTpiUpdate(null); setSaveState('idle') }}
      />
    </>
  )
}
export default TPISection
